if (!customElements.get('mega-search-layout')) {
  customElements.define('mega-search-layout', class extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.main = document.getElementById('MainContent');
      if (!this.main || document.body.dataset.template !== 'search') return;
      this.categories = this.querySelector('.mm-search-categories');
      this.advice = this.querySelector('.mm-search-advice');
      this.moreLinks = this.querySelector('.mm-search-more-links');
      this.secondaryNav = this.querySelector('.mm-search-secondary-nav');
      this.cache = new Map();
      this.query = null;
      this.queue = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.refresh(); });
      };
      this.observer = new MutationObserver(this.queue);
      this.observer.observe(this.main, { childList: true, subtree: true });
      this.navigate = (event) => {
        const button = event.target.closest('[data-mm-view]');
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();
        const tab = this.main.querySelector(`#boost-sd__search-result-toolbar-panel-${button.dataset.mmView}`);
        if (!tab) return;
        tab.click();
        this.queue();
        this.focusAfterView = true;
      };
      this.main.addEventListener('click', this.navigate, true);
      window.addEventListener('popstate', this.queue);
      this.refresh();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
      this.main?.removeEventListener('click', this.navigate, true);
      window.removeEventListener('popstate', this.queue);
      for (const element of [this.categories, this.moreLinks, this.advice, this.secondaryNav]) element?.remove();
      document.body.classList.remove('mm-search-enhanced', 'mm-search-related-ready');
      this.started = false;
    }
    refresh() {
      const container = this.main.querySelector('.boost-sd-container-search-page');
      const header = container?.querySelector('.boost-sd__search-form-wrapper');
      const layout = container?.querySelector('.boost-sd-layout');
      if (!header || !layout) return;
      const active = container.querySelector('.boost-sd__search-result-toolbar-panel--active');
      const secondary = !!active && !active.id.endsWith('-products');
      if (header.nextElementSibling !== this.categories) header.after(this.categories);
      if (this.categories.nextElementSibling !== this.moreLinks) this.categories.after(this.moreLinks);
      if (this.moreLinks.nextElementSibling !== this.secondaryNav) this.moreLinks.after(this.secondaryNav);
      if (layout.nextElementSibling !== this.advice) layout.after(this.advice);
      this.secondaryNav.hidden = !secondary;
      this.moreLinks.hidden = secondary || (!this.hasCategories && !this.hasPages);
      this.moreLinks.querySelector('[data-mm-view=collections]').hidden = !this.hasCategories;
      this.moreLinks.querySelector('[data-mm-view=pages]').hidden = !this.hasPages;
      this.categories.hidden = secondary || !this.hasCategories;
      this.advice.hidden = secondary || !this.hasPages;
      document.body.classList.add('mm-search-enhanced');
      if (this.focusAfterView && !container.querySelector('[aria-busy="true"]')) {
        this.focusAfterView = false;
        header.scrollIntoView({ block: 'start', behavior: 'instant' });
        const desktopSearch = document.querySelector('#header-group .search-desktop-nav__button');
        const target = secondary ? this.secondaryNav.querySelector('button') : desktopSearch?.getClientRects().length ? desktopSearch : header.querySelector('input');
        target?.focus({ preventScroll: true });
      }
      const query = new URLSearchParams(location.search).get('q')?.trim() || '';
      this.updateHeader(query);
      if (query === this.query) return;
      this.query = query;
      this.controller?.abort();
      this.hasCategories = false;
      this.hasPages = false;
      this.categories.hidden = true;
      this.advice.hidden = true;
      document.body.classList.remove('mm-search-related-ready');
      if (!query) return;
      if (this.cache.has(query)) { this.renderRelated(this.cache.get(query)); return; }
      this.loadRelated(query);
    }
    updateHeader(query) {
      const button = document.querySelector('#header-group .search-desktop-nav__button');
      if (!button) return;
      const label = query || this.dataset.searchLabel;
      const text = [...button.childNodes].find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (text && text.textContent.trim() !== label) text.textContent = ` ${label}`;
    }
    safeUrl(value) {
      try {
        const url = new URL(value, location.origin);
        if (url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol)) return null;
        const locale = this.dataset.root.replace(/\/$/, '');
        if (locale && !url.pathname.startsWith(`${locale}/`)) url.pathname = locale + url.pathname;
        return url.pathname + url.search + url.hash;
      } catch { return null; }
    }
    async loadRelated(query) {
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL('https://services.mybcapps.com/bc-sf-filter/search/suggest');
        url.search = new URLSearchParams({ shop: this.dataset.shop, q: query, collection_limit: '6', page_limit: '4', product_limit: '1', suggestion_limit: '1' });
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Search suggestions unavailable');
        const data = await response.json();
        if (data.error || !Array.isArray(data.collections) || !Array.isArray(data.pages)) throw new Error('Invalid search suggestions');
        if (controller.signal.aborted || query !== this.query || !this.isConnected) return;
        if (this.cache.size >= 12) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, data);
        this.renderRelated(data);
      } catch {
        if (query === this.query && this.isConnected) document.body.classList.remove('mm-search-related-ready');
      } finally { clearTimeout(timeout); }
    }
    renderRelated(data) {
      const categoryLinks = this.categories.querySelector('.mm-search-category-links');
      const pageLinks = this.advice.querySelector('.mm-search-advice-links');
      categoryLinks.replaceChildren();
      pageLinks.replaceChildren();
      for (const item of data.collections.slice(0, 6)) {
        if (!item.handle || !item.title) continue;
        const href = this.safeUrl(`/collections/${encodeURIComponent(item.handle)}`);
        if (!href) continue;
        const link = document.createElement('a');
        link.href = href;
        link.textContent = item.title;
        categoryLinks.appendChild(link);
      }
      for (const item of data.pages.slice(0, 4)) {
        if (!item.url || !item.title) continue;
        const href = this.safeUrl(item.url);
        if (!href) continue;
        const link = document.createElement('a');
        link.href = href;
        const label = document.createElement('span');
        label.textContent = href.includes('/blogs/') ? this.dataset.articleLabel : this.dataset.pageLabel;
        const title = document.createElement('div');
        title.textContent = item.title;
        link.append(label, title);
        pageLinks.appendChild(link);
      }
      this.hasCategories = !!categoryLinks.children.length;
      this.hasPages = !!pageLinks.children.length;
      document.body.classList.add('mm-search-related-ready');
      this.queue();
    }
  });
}
